import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { conversationKeys } from '@/entities/messaging/hooks';
import { createSSEConnection } from '@/shared/api/sseClient';
import type { SSEEvent } from '@/shared/api/sseClient';

const NOTIFICATION_KEY = ['notifications'];
const BADGE_KEY = ['badges'];
const MAX_RETRIES = 5;
const BASE_DELAY = 1000;
const MAX_DELAY = 30_000;

export function useSSE(enabled: boolean = false) {
  const queryClient = useQueryClient();
  const connectionRef = useRef<{ disconnect: () => void } | null>(null);
  const retriesRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const handleEvent = useCallback(
    (event: SSEEvent) => {
      retriesRef.current = 0;

      switch (event.type) {
        case 'new_message':
          queryClient.invalidateQueries({ queryKey: conversationKeys.all() });
          if ((event.data as any)?.conversation_id) {
            queryClient.invalidateQueries({
              queryKey: conversationKeys.detail((event.data as any).conversation_id),
            });
          }
          queryClient.invalidateQueries({ queryKey: BADGE_KEY });
          break;

        case 'message_read':
          if ((event.data as any)?.conversation_id) {
            queryClient.invalidateQueries({
              queryKey: conversationKeys.detail((event.data as any).conversation_id),
            });
          }
          break;

        case 'conversation_updated':
          queryClient.invalidateQueries({ queryKey: conversationKeys.all() });
          break;

        case 'notification':
          queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEY });
          queryClient.invalidateQueries({ queryKey: BADGE_KEY });
          break;

        case 'badge_update':
          queryClient.invalidateQueries({ queryKey: BADGE_KEY });
          break;

        default:
          break;
      }
    },
    [queryClient]
  );

  const connect = useCallback(() => {
    if (!enabledRef.current) return;
    if (connectionRef.current) {
      connectionRef.current.disconnect();
      connectionRef.current = null;
    }

    connectionRef.current = createSSEConnection(handleEvent, () => {
      connectionRef.current = null;
      retriesRef.current += 1;

      if (retriesRef.current > MAX_RETRIES) {
        return;
      }

      const delay = Math.min(BASE_DELAY * 2 ** (retriesRef.current - 1), MAX_DELAY);
      timerRef.current = setTimeout(() => {
        if (enabledRef.current) connect();
      }, delay);
    });
  }, [handleEvent]);

  useEffect(() => {
    if (enabled) {
      retriesRef.current = 0;
      connect();
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (connectionRef.current) {
        connectionRef.current.disconnect();
        connectionRef.current = null;
      }
    };
  }, [enabled, connect]);
}
